import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { 
  LayoutDashboard, CheckSquare, Clock, Users, FileText, 
  Receipt, Settings, LogOut, Menu, X, ChevronLeft, Mic, Calendar,
  DollarSign, Activity, Bot, Building, Target, Phone
} from 'lucide-react';

const navItems = [
  { icon: LayoutDashboard, label: 'Dashboard', href: '/' },
  { icon: Calendar, label: 'Calendario', href: '/calendar' },
  { icon: Activity, label: 'Productividad', href: '/productivity' },
  { icon: Bot, label: 'Asistente AI', href: '/ai-assistant' },
  { icon: CheckSquare, label: 'Tareas', href: '/tasks' },
  { icon: Clock, label: 'Time Tracker', href: '/time' },
  { icon: Building, label: 'Empresas', href: '/empresas' },
  { icon: Target, label: 'Negocios', href: '/negocios' },
  { icon: Phone, label: 'Llamadas', href: '/llamadas' },
  { icon: Users, label: 'CRM', href: '/crm' },
  { icon: FileText, label: 'Cotizaciones', href: '/quotes' },
  { icon: Receipt, label: 'Facturación', href: '/invoices' },
  { icon: DollarSign, label: 'Costos', href: '/costs' },
  { icon: Mic, label: 'Reuniones', href: '/meetings' },
  { icon: Settings, label: 'Configuración', href: '/settings' },
];

function Sidebar({ collapsed, onCollapse }) {
  const location = useLocation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <aside 
      className={cn(
        "fixed left-0 top-0 z-40 h-screen bg-card border-r transition-all duration-300",
        collapsed ? "w-16" : "w-64"
      )}
    >
      <div className="flex flex-col h-full">
        {/* Logo */}
        <div className="flex items-center justify-between h-16 px-4 border-b">
          {!collapsed && (
            <div className="flex items-center gap-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_teamflow-manage-1/artifacts/krciwypq_2024%20Logo%20TRANSTAR%20.jpg" 
                alt="Transtar" 
                className="h-8"
              />
              <span className="font-heading text-lg font-bold text-primary">HU</span>
            </div>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={onCollapse}
            className="h-8 w-8"
            data-testid="collapse-sidebar-btn"
          >
            <ChevronLeft className={cn("h-4 w-4 transition-transform", collapsed && "rotate-180")} />
          </Button>
        </div>

        {/* Navigation */}
        <ScrollArea className="flex-1 py-4">
          <nav className="space-y-1 px-2">
            {navItems.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  className={cn(
                    "flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors",
                    isActive 
                      ? "bg-primary text-primary-foreground" 
                      : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                  )}
                >
                  <item.icon className="h-5 w-5 flex-shrink-0" />
                  {!collapsed && <span>{item.label}</span>}
                </Link>
              );
            })}
          </nav>
        </ScrollArea>

        {/* User section */}
        <div className="border-t p-4">
          {!collapsed && user && (
            <div className="mb-3">
              <p className="text-sm font-medium truncate">{user.name}</p>
              <p className="text-xs text-muted-foreground truncate">{user.email}</p>
            </div>
          )}
          <Button
            variant="ghost"
            size={collapsed ? "icon" : "default"}
            onClick={handleLogout}
            className={cn("w-full", collapsed ? "justify-center" : "justify-start")}
            data-testid="logout-btn"
          >
            <LogOut className="h-4 w-4" />
            {!collapsed && <span className="ml-2">Cerrar Sesión</span>}
          </Button>
        </div>
      </div>
    </aside>
  );
}

function MobileNav({ open, onClose }) {
  const location = useLocation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
    onClose();
  };

  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-40" onClick={onClose} />
      <aside className="fixed left-0 top-0 z-50 h-screen w-64 bg-card border-r animate-in slide-in-from-left">
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between h-16 px-4 border-b">
            <div className="flex items-center gap-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_teamflow-manage-1/artifacts/krciwypq_2024%20Logo%20TRANSTAR%20.jpg" 
                alt="Transtar" 
                className="h-8"
              />
              <span className="font-heading text-lg font-bold text-primary">HU</span>
            </div>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="h-5 w-5" />
            </Button>
          </div>

          <ScrollArea className="flex-1 py-4">
            <nav className="space-y-1 px-2">
              {navItems.map((item) => {
                const isActive = location.pathname === item.href;
                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    onClick={onClose}
                    className={cn(
                      "flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors",
                      isActive 
                        ? "bg-primary text-primary-foreground" 
                        : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                    )}
                  >
                    <item.icon className="h-5 w-5" />
                    <span>{item.label}</span>
                  </Link>
                );
              })}
            </nav>
          </ScrollArea>

          <div className="border-t p-4">
            {user && (
              <div className="mb-3">
                <p className="text-sm font-medium truncate">{user.name}</p>
                <p className="text-xs text-muted-foreground truncate">{user.email}</p>
              </div>
            )}
            <Button
              variant="ghost"
              className="w-full justify-start"
              onClick={handleLogout}
            >
              <LogOut className="h-4 w-4 mr-2" />
              Cerrar Sesión
            </Button>
          </div>
        </div>
      </aside>
    </>
  );
}

export default function Layout({ children }) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop Sidebar */}
      <div className="hidden md:block">
        <Sidebar collapsed={collapsed} onCollapse={() => setCollapsed(!collapsed)} />
      </div>

      {/* Mobile Nav */}
      <div className="md:hidden">
        <MobileNav open={mobileOpen} onClose={() => setMobileOpen(false)} />
      </div>

      {/* Main Content */}
      <main 
        className={cn(
          "min-h-screen transition-all duration-300",
          collapsed ? "md:ml-16" : "md:ml-64"
        )}
      >
        {/* Mobile Header */}
        <header className="md:hidden sticky top-0 z-30 flex items-center h-14 px-4 border-b bg-background">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setMobileOpen(true)}
            data-testid="mobile-menu-btn"
          >
            <Menu className="h-5 w-5" />
          </Button>
          <div className="ml-3 flex items-center gap-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_teamflow-manage-1/artifacts/krciwypq_2024%20Logo%20TRANSTAR%20.jpg" 
              alt="Transtar" 
              className="h-6"
            />
            <span className="font-heading text-lg font-bold text-primary">HU</span>
          </div>
        </header>

        {/* Page Content */}
        <div className="p-4 md:p-6 lg:p-8">
          {children}
        </div>
      </main>
    </div>
  );
}
